/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, ReplaySubject } from 'rxjs';
import { RecurringJob } from '@src/app/model/recurring-job';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { Omc } from '@src/app/model/Omc';
import { User } from '@src/app/model/user';
import { Field } from '@src/app/shared/field/Field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { removeError, required } from '@src/app/shared/validators';
import { OmcService } from '@src/app/modules/agent/omc.service';
import { UserService } from '@src/app/modules/user/user.service';
import { RequestTemplatesService } from '@src/app/modules/crm/request-templates.service';
import { RecurringJobService } from '@src/app/modules/crm/recurring-job/recurring-job.service';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { AbstractObject } from '@src/app/model/abstract-object';
import { HttpParams } from '@angular/common/http';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { RecurringJobEditor } from '@src/app/modules/crm/recurring-job/recurring-job-editor';
import { ServiceRequestPageComponent } from '@src/app/modules/crm/service-request-page/service-request-page.component';
import { RequestService } from '@src/app/modules/crm/request.service';
import { MessageService, MsgDef } from '@src/app/shared/message.service';
import { NewRequestData, RequestContextService } from '@src/app/modules/crm/request-context.service';
import { ValidationErrors } from '@angular/forms';
import { first, map } from 'rxjs/operators';
import { ErrorCode } from '@src/app/shared/form/form-error/form-error.component';
import { RequestTemplate } from '@src/app/model/RequestTemplate';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { DateHelper } from '@src/app/shared/dateHelper';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { RecurringJobPageNavRoute } from '@src/app/routes/recurring-job-page-nav';
import { SupplierPageNavRoute } from '@src/app/routes/supplier-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';

@Component({
    selector: 'app-recurring-job',
    templateUrl: './recurring-job.component.html',
    styleUrls: ['./recurring-job.component.scss'],
    imports: [FormPageComponent]
})
export class RecurringJobComponent extends AbstractPageComponent {
    private dataSvc = inject(RecurringJobService);
    private omcSvc = inject(OmcService);
    private router = inject(Router);
    private dialog = inject(MatDialog);
    private reqContextSvc = inject(RequestContextService);
    private requestSvc = inject(RequestService);
    private tmplSvc = inject(RequestTemplatesService);
    private msgSvc = inject(MessageService);
    dialogRef = inject<MatDialogRef<RecurringJobComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    cds = inject(ConfirmDialogService);

    users: User[] = [];

    omcField = FormPicklistComponent.make('Owner Management Company', 'omcId', 'omc',
        { service: this.omcSvc, refreshes: [this.setTeamId.bind(this)] }, {
            validators: [required],  asyncValidators: [this.validateOMC.bind(this)]
    });

    teamField: Field = FieldMaker.idHolder('teamId');
    templateField = FormPicklistComponent.make('Standard Request', 'templateId', 'template', { service: this.tmplSvc },
        { validators: [required], asyncValidators: [this.validateTemplate.bind(this)]
    });

    /* Last Status Unknown without completex queries
    lastSubStatusField = ServiceRequest.getSubStatusField().override({value: 'lastRequest.subStatus'});
    */
    lastStatusField = FormPicklistComponent.make('Last Status', 'lastRequest.srStatus', null,
        { items: ServiceRequest.srStates, },
        { readonly: true, visible: Field.noForm, sendServer: false }
    );

    lastPlannedField = FormDateComponent.make('Last Planned', 'lastRequest.plannedDate',
        {readonly: true, sendServer: false, formColumn: 3, visible: Field.noForm}
    );

    lastOwnerField = FormPicklistComponent.make('Last Owner', 'lastRequest.ownedById', null,
        { items: this.users },
        {
            formColumn: 1, readonly: true, sendServer: false, visible: Field.noForm,
        }
    );

    /* requestGrid: GridField = new GridField(
        {
            field: { value: 'occurences', readonly: true, cellOpts: { heading: 'Requests' }, formRow: 2, sendServer: false, visible: Field.formOnly, },
            rowFactory: () => [
                FieldMaker.id(),
                ServiceRequest.getRefField(),
                ServiceRequest.getTitleLinkField(),
                ServiceRequest.getDescriptionField(),
                ServiceRequest.getStateField(),
                FormDateComponent.make('Planned Date', 'plannedDate', {readonly: true}),
                ServiceRequest.getCreatedField(),
                ServiceRequest.getLastUpdatedField(),
                ServiceRequest.getOwnedByROField()
            ],
        }
    );
    */

    lastRequestLink = FormButtonComponent.makeTableButton('Last Request', 'refNr', (item) => {

        const job = (item as RecurringJob);
        if (job.lastRequest?.id) {
            this.dialog.open(ServiceRequestPageComponent,
                {
                    data: {
                        id: job.lastRequest.id,
                    }
                }
            ).afterClosed().subscribe(() => {
                this.requestSvc.getOne(job.lastRequest.id, null).subscribe((request) => {
                    job.lastRequest = request;
                });
            });
        }
    }, {
        calculateValue: (o: RecurringJob) => o?.lastRequest?.refNr,
        visible: Field.noForm
    });

    supplierField = FormNavLinkComponent.makeDetail('Supplier', 'supplier.name', 'supplierId', SupplierPageNavRoute);
    supplierNewRequestField = FormButtonComponent.makeTableButton('+', 'newSupplierRequest',
        (item) => this.createNewSupplierRequest(item as RecurringJob), {
        calculateValue: (rj: RecurringJob) => rj.supplierId ? 'add' : '',
        visible: Field.noForm, type: 'icon',
        toolTip: $localize`Create a new request for this supplier, for something other than to perform this regular job`
    });

    supplierOpenLink = FormNavLinkComponent.make(' ',
        ServiceRequestPageNavRoute, (o: RecurringJob) => ({ omcId: o.omcId, supplierId: o.supplierId, _sort: 'refNr' }),
        { visible:Field.noForm, value: 'supplier_open_count',
            calculateValue: (o: RecurringJob) => o.supplier_open_count > 0 ? o.supplier_open_count: ""
            , cellOpts: { style: 'text-align: center' }, 
            toolTip: $localize `Number of open requests for specified supplier within the OMC` }
    );    

    occurencesLink = FormNavLinkComponent.make('Occurrences',
        ServiceRequestPageNavRoute, o => ({ jobId: o.id, _sort: 'refNr' }),
        { visible:Field.noForm, value: 'occurences_count', cellOpts: { style: 'text-align: center' } }
    );
    yearOccurences = FormNumberComponent.make('Prev Year', 'yearOccurences').override({cellOpts: {style: 'text-align:center'}});

    configReady = new ReplaySubject<null>(1);

    /* timesDoneField = FormNumberComponent.make('Times Done', 'timesDone', {}, {
        label: 'Times Done', cellOpts: {heading: 'Times Done'}, disable: true, visible: Field.formOnly,
    }); */

    propertyManagerField = FormPicklistComponent.make('Property Manager', 'omc.managerId', null,
        { items: this.users },
        { formColumn: 1, readonly: true, sendServer: false, visible: Field.noForm,
            calculateValue: o => (o as RecurringJob).omc?.managerId
        }
    );

    propertyAdminField = FormPicklistComponent.make('Admin', 'omc.adminId', null,
        { items: this.users },
        { formColumn: 1, readonly: true, sendServer: false, visible: Field.noForm,
            calculateValue: o => (o as RecurringJob).omc?.adminId
        }
    );

    propertyFinanceField = FormPicklistComponent.make('Finance', 'omc.financeId', null,
        { items: this.users },
        {
            formColumn: 1, readonly: true, sendServer: false, visible: Field.noForm,
            calculateValue: o => (o as RecurringJob).omc?.financeId
        }
    );

    frequencyPicklist = RecurringJob.getFrequencyField().override({visible: Field.formOnly});

    editLink = FormButtonComponent.makeTableButton('Edit', 'Edit', (item) => {
        new RecurringJobEditor(item as RecurringJob).editJob();
    }, {
        visible: Field.noForm, calculateValue: () => 'edit',
        type: 'icon'
    });

    frequencyField = FormTextComponent.make('Frequency', 'frequency.name', {sendServer: false, readonly: true, visible:Field.noForm});

    nextOccurenceField = FormDateComponent.make('Next Occurrence', 'nextOccurence', {
        formColumn: 2, hint: "Date this next needs to be planned",
    });

    nextFieldTable = FormButtonComponent.makeTableButton('Create Next Request', 'whatever',
        (item) => this.createNext(item as RecurringJob), {
            calculateValue: (o: RecurringJob) => o.lastRequest?.srStatus === 'open' ? '' : 'add_circle' ,
            visible: Field.noForm, type: 'icon',
            toolTip: $localize `Create the next request for this recurring job`
    });

    deleteField = FormButtonComponent.makeTableButton('Delete', 'delete',
        (item) => this.delete(item as RecurringJob), {
        calculateValue: (o: RecurringJob) => o.lastRequest ? '' : 'delete',
        visible: Field.noForm, type: 'icon',
        toolTip: $localize`Delete usuned recurring job`,
        tableIconStyler: () => ({color:'red'}),
    });

    lastCalcDateStatus(rj: RecurringJob) {
        if (!rj.lastRequest || rj.lastRequest.srStatus !== ServiceRequest.srOpen.id) {
            return '';
        } else if (!rj.lastRequest.plannedDate) {
            return 'event_busy';
        }
        const diff = DateHelper.daysFromToday(rj.lastRequest.plannedDate);
        if (diff < 0) {
            return 'event_busy';
        } else if (diff < 14) {
            return 'schedule'; 
        } else {
            return '';
        }
    }
    lastCalcDateStatusStyler(rj: RecurringJob) {
        if (!rj.lastRequest?.plannedDate) {
            return {'color': 'red'};
        } else if (rj.lastRequest.srStatus !== ServiceRequest.srOpen.id) {
            return {};
        }
        const diff = DateHelper.daysFromToday(rj.lastRequest.plannedDate);
        if (diff < -14) {
            return {'background-color': 'red', 'color':'white'};
        } else if (diff < -7) {
            return {'background-color': 'orange', 'color':'white'};
        } else if (diff < 0) {
            return {'color': 'red'};
        } else if (diff < 14) {
            return {'color': 'orange'};
        } else {
            return {'color': 'green'};
        }
    }

    lastPlannedDateIndicator = FormButtonComponent.make('', '', {
        name: 'lastPlannedDateIndicator', type: 'icon', sendServer: false, label: '', readonly: true,
        cellOpts: { heading: '', width: '1%', style: 'padding: 0px 0px 0px 0px; ' },
        calculateValue: this.lastCalcDateStatus.bind(this),
        tableIconStyler: this.lastCalcDateStatusStyler.bind(this),
        toolTip: $localize `Status of last request you created for this job. Large Red 14 days overdue, Large Orange 7 days overdue, small red overdue, small orange due in next 14 days`
    });   

    calcDateStatus(rj: RecurringJob) {
        if (!rj.nextOccurence) {
            return 'event_busy';
        }
        const diff = DateHelper.daysFromToday(rj.nextOccurence);
        if (diff < 0) {
            return 'event_busy';
        } else if (diff < 14) {
            return 'schedule'; 
        } else {
            return '';
        }
    }
    calcDateStatusStyler(rj: RecurringJob) {
        if (!rj.nextOccurence) {
            return {'color': 'red'};
        }
        const diff = DateHelper.daysFromToday(rj.nextOccurence);
        if (diff < 0) {
            return {'background-color': 'red', 'color':'white'};
        } else if (diff < 7) {
            return {'color': 'red'}; //schedule            
        } else if (diff < 14) {
            return {'color': 'orange'}; //schedule
        } else {
            return {'color': 'green'};
        }
    }

    plannedDateIndicator = FormButtonComponent.make('', '', {
        name: 'plannedDateIndicator', type: 'icon', sendServer: false, label: '', readonly: true,
        cellOpts: { heading: '', width: '1%', style: 'padding: 0px 0px 0px 0px; ' },
        calculateValue: this.calcDateStatus.bind(this),
        tableIconStyler: this.calcDateStatusStyler.bind(this),
        toolTip: $localize `Do you need to create a new occurence of this job? Large Red Overdue, Red needed in next 7 days or date missing, Orange needed in next 14 days. When you create an occurence of this job, the system will guess when you next need to schedule the a request to do it again`
    });

    calcFrequencyStatus(rj: RecurringJob) {
        if (+rj.frequencyId <= 1) {
            return '';
        }
        const diff = +rj.yearOccurences / +rj.frequencyId;
        if (diff < .5) {
            return 'error_outline';
        } else if (diff < 1) {
            return 'hourglass_bottom'; 
        } else {
            return '';
        }
    }
    calcFrequencyStyler(rj: RecurringJob) {
        if (+rj.frequencyId <= 1) {
            return '';
        }
        const diff = +rj.yearOccurences / +rj.frequencyId;
        if (diff < .5 && (!rj.lastRequest || rj.lastRequest.srStatus !== 'open')) {
            return {'background-color': 'red', 'color': 'white'}
        } else if (diff < .5) {
            return {'color':'red'};
        } else if (diff < 1 && (!rj.lastRequest || rj.lastRequest.srStatus !== 'open')) {
            return {'background-color': 'orange', 'color': 'white'}
        } else if (diff < 1) {
            return {'color':'orange'};
        } else {
            return {};
        }
    }

    frequencyIndicator = FormButtonComponent.make('', '', {
        name: 'frequencyIndicator', type: 'icon', sendServer: false, label: '', readonly: true,
        cellOpts: { heading: '', width: '1%', style: 'padding: 0px 0px 0px 0px; ' },
        calculateValue: this.calcFrequencyStatus.bind(this),
        tableIconStyler: this.calcFrequencyStyler.bind(this),
        toolTip: $localize `Have you done this job regularly enough this year? Large ICON means needs occurence created, small icon, not enough occurences this year. Red: less than half occurences credted, Orange not enough occurences created.`
    });    

    config = new FormConfig({
        navRoute: RecurringJobPageNavRoute,
        docPath: 'omc-professional/requests/recurring-jobs',
        title: $localize`Recurring Job`,
        help: $localize`Recurring Jobs`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                this.teamField,
                this.omcField,
                this.templateField,
                this.supplierField,
                this.supplierOpenLink,
                this.supplierNewRequestField,
                this.frequencyIndicator,
                this.frequencyPicklist,
                this.frequencyField,
                this.yearOccurences,
                this.nextOccurenceField,
                this.plannedDateIndicator,
                this.editLink,
                this.lastRequestLink,
                this.lastStatusField,
                this.nextFieldTable,
                this.lastPlannedField,
                this.lastPlannedDateIndicator,                
                this.lastOwnerField,
                this.occurencesLink,
                this.propertyManagerField,
                this.propertyAdminField,
                this.propertyFinanceField,
                //ServiceRequest.getTitleLinkField().override({value: 'last.title'}),
                this.deleteField,
                FieldMaker.notes({formColumn: 3, cellOpts: {width: '50%'}}),
                // this.requestGrid,

            ],
            formLayout: [{ cells: [{ width: '25%' }, { width: '25%' }, { width: '50%' }] },
                         { cells: [{ colspan: 4, pageTab: 'yes' }] }
            ],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.beforeNew.bind(this),
        beforeEdit: this.beforeEdit.bind(this),
        beforeList: this.beforeList.bind(this),
        configReady:  this.configReady,
        //actions: [new RequestJobAction(this.router) ],
        afterSave: () => {
            history.back();
        }
    });

    beforeNew() : Observable<RecurringJob>{
        this.frequencyPicklist.visible = Field.formOnly;
        return of(new RecurringJob());
    }

    setTeamId(omc: Omc) {
        this.teamField.control.setValue(omc.omcTeamId, {emitEvent: false});
    }

    beforeEdit(rj: RecurringJob) {
        if (rj && rj.occurences) {
            rj.timesDone = rj.occurences.length;
        } else {
            rj.timesDone = 0;
        }
        if (!rj.occurences) {
            rj.occurences = [];
        }
        return rj;
    }

    constructor() {
        const teamUserSvc = inject(UserService);


        super();

        this.subscriptions.push(teamUserSvc.getUsers().pipe(first()).subscribe(
            (users) => {
                /* Emptied rather than reassigned - four picklists hold this array as their items */
                this.users.length = 0;
                for (const u of users as User[]) {
                    this.users.push(u);
                }

                this.configReady.next(null)
            }
        ));
    }

    validateOMC() : Observable<ValidationErrors | null> {

        const omcId = this.omcField.control.value;
        if (!omcId) {
            return of(null);
        }
        const omc = this.omcField.picklist.items.find( o => o.id === omcId) as Omc;
        if (!omc) {
            return of(new ErrorCode('InvalidOMC', 'Invalid OMC').new());
        }

        const template = (this.templateField.picklist.valueObject as RequestTemplate);
        if (!template) {
            return of(null);
        }

        const parms = new HttpParams().set('templateId', template.id);
        const errCode = 'templateExists';

        return this.dataSvc.get(false, parms, omc.omcTeamId).pipe( map( (result) => {
            if (result && result.length > 0) {
                return new ErrorCode(errCode, 'Already has job for ' + template.name).new()
            } else {
                removeError(this.templateField.control, errCode);
                removeError(this.omcField.control, errCode);
            }
            return null;
        }));
    }

    validateTemplate() : Observable<ValidationErrors | null> {

        const omc = (this.omcField.picklist.valueObject as Omc);
        if (!omc) {
            return of(null);
        }
        const templateId = this.templateField.control.value;
        if (!templateId) {
            return of(null);
        }

        const parms = new HttpParams().set('templateId', templateId);
        const errCode = 'templateExists';

        return this.dataSvc.get(false, parms, omc.omcTeamId).pipe( map( (result) => {
            if (result && result.length > 0) {
                return new ErrorCode(errCode, 'Already Exists for ' + omc.name).new()
            } else {
                removeError(this.templateField.control, errCode);
                removeError(this.omcField.control, errCode);
            }
            return null;
        }));
    }

    /**
     * Work for this job's supplier that is not the job itself - so deliberately no template: the
     * standard request is what "Create Next" raises, and this is everything else.
     */
    createNewSupplierRequest(rj: RecurringJob) {
        if (rj.supplierId) {
            this.openStepper(rj, { requestType: 'workOrder', supplierId: rj.supplierId })
                .subscribe((newSr) => {
                    if (newSr) {
                        rj.supplier_open_count += 1;
                    }
                });
        } else {
            const msg = $localize`Can only add requests here if the supplier is entered for the job`;
            const msgDef = new MsgDef(msg, 'warn');

            this.msgSvc.show(msgDef);
        }
    }

    delete(rj: RecurringJob) {
        if (+rj.occurences_count === 0 && !rj.lastRequest && !rj.nextOccurence) {
            const title = $localize `Delete Job`;
            const msg = $localize `Delete ${rj.template?.name} ${rj.frequency.name} recurring job?`;
            this.cds.open(title, msg, () => {
                this.dataSvc.delete(rj).subscribe(result => {
                    if (result) {
                        rj.omc.name = '*** Deleted ****';
                        rj.template.name = '*** Deleted ***';
                        rj.frequency.name = '*** Deleted ***';
                    }
                });
            });
        } else {
            this.cds.alert('Job Used', 'Cannot delete a job that has had requests created against it');
        }
    }

    createNext(rj: RecurringJob) {
        if (rj.lastRequest?.srStatus === 'open') {
            const msg = $localize`Please complete Service Request ${rj.lastRequest.refNr} before creating next`;
            const msgDef = new MsgDef(msg, 'warn');

            this.msgSvc.show(msgDef);
            return;
        }

        // A job with a supplier is work being ordered from them, so it is raised as a work order -
        // the server reads the same thing from the supplier either way.
        this.openStepper(rj, {
            requestType: rj.supplierId ? 'workOrder' : 'case',
            templateId: rj.templateId,
            supplierId: rj.supplierId,
            jobId: rj.id,
            plannedDate: rj.nextOccurence,
        }).subscribe(() => {
            this.dataSvc.getOne(rj.id, null).subscribe(updatedRj => {
                if (updatedRj) {
                    Field.shallowCopy(updatedRj, rj);
                }
            });
        })
    }

    /**
     * The job names the OMC it runs for, and a recurring job's teamId is that OMC's own team, so
     * naming it here is what saves the user being asked which OMC.
     */
    private openStepper(rj: RecurringJob, data: NewRequestData) {
        return this.reqContextSvc.newRequest({ ...data, teamId: rj.teamId });
    }

    beforeList(data: AbstractObject[], params: HttpParams) {

        if (params.has('omcId')) {
            if (!this.propertyManagerField.filter.active) {
                this.propertyManagerField.visible.computer = false;
                this.propertyManagerField.visible.phone = false;
                this.propertyAdminField.visible.computer = false;
                this.propertyAdminField.visible.phone = false;
                this.propertyFinanceField.visible.computer = false;
                this.propertyFinanceField.visible.phone = false;
            }
        } else {
            if (!this.propertyManagerField.filter.active) {
                this.propertyManagerField.visible.computer = true;
                this.propertyManagerField.visible.phone = true;
                this.propertyAdminField.visible.computer = true;
                this.propertyAdminField.visible.phone = true;
                this.propertyFinanceField.visible.computer = true;
                this.propertyFinanceField.visible.phone = true;
            }
        }
        return data;
    }

}